import {Accordion, AccordionDetails, AccordionSummary, Typography} from "@mui/material"
import React from "react"
import styled from "styled-components"
import {ExpandMore as ExpandMoreIcon, Info as InfoIcon} from "@mui/icons-material"

import {CreatedActivity} from "./CreatedActivity"
import {SMSSentActivity} from "./SMSSentActivity"
import {SMSFailedActivity} from "./SMSFailedActivity"
import {EmailSentActivity} from "./EmailSentActivity"
import {NOALetterSentActivity} from "./NOALetterSentActivity"
import {AddressChangedActivity} from "./AddressChangedActivity"
import {CaseEscalatedActivity} from "./CaseEscalatedActivity"
import {CaseSubmittedActivity} from "./CaseSubmittedActivity"
import {CaseResolvedActivity} from "./CaseResolvedActivity"
import {CommunicationSnoozedActivity} from "./CommunicationSnoozedActivity"
import {EmailAddedActivity} from "./EmailAddedActivity"
import {EmailRemovedActivity} from "./EmailRemovedActivity"
import {PhoneAddedActivity} from "./PhoneAddedActivity"
import {PhoneRemovedActivity} from "./PhoneRemovedActivity"
import {PhoneIdentityScrubActivity} from "./PhoneIdentityScrubActivity"
import {InformationUpdatedActivity} from "./InformationUpdatedActivity"
import {EmailCommunicationsOptedOutActivity} from "./EmailCommunicationsOptedOutActivity"
import {EmailCommunicationsOptedInActivity} from "./EmailCommunicationsOptedInActivity"
import {PreferredLanguageConfiguredActivity} from "./PreferredLanguageConfiguredActivity"
import {DigitalCommunicationConsentGrantedActivity} from "./DigitalCommunicationConsentGrantedActivity"
import {DigitalCommunicationConsentDeniedActivity} from "./DigitalCommunicationConsentDeniedActivity"
import {ESIGNConsentRequestedActivity} from "./ESIGNConsentRequestedActivity"
import {ESIGNConsentGrantedActivity} from "./ESIGNConsentGrantedActivity"
import {ESIGNConsentRevokedActivity} from "./ESIGNConsentRevokedActivity"
import {EmailLeadsAddedActivity} from "./EmailLeadsAddedActivity"
import {EmailLeadRemovedActivity} from "./EmailLeadRemovedActivity"
import {PhoneOptedOutForSMSActivity} from "./PhoneOptedOutForSMSActivity"
import {PhoneReassignedNumberActivity} from "./PhoneReassignedNumberActivity"
import {PaymentPlanSetupSucceededActivity} from "./PaymentPlanSetupSucceededActivity"
import {PaymentPlanCompletedActivity} from "./PaymentPlanCompletedActivity"
import {PaymentPlanCancelledActivity} from "./PaymentPlanCancelledActivity"
import {PaymentArrangementSetupSucceededActivity} from "./PaymentArrangementSetupSucceededActivity"
import {PaymentArrangementCompletedActivity} from "./PaymentArrangementCompletedActivity"
import {PaymentArrangementCancelledActivity} from "./PaymentArrangementCancelledActivity"
import {InstalmentOverrideAppliedActivity} from "./InstalmentOverrideAppliedActivity"
import {InstalmentOverrideResetActivity} from "./InstalmentOverrideResetActivity"

import {CustomerActivitiesResponse, CustomerActivity} from "@indebted/api"

const classes = {
	root: "activity-list-root",
	summary: "activity-summary",
	activityContent: "activity-content",
	activityContentLabel: "activity-content-label",
	activityContentValue: "activity-content-value",
	jsonContent: "json-content",
	jsonContentInfo: "json-content-info",
}

function List({activities, customerID}: {activities: CustomerActivitiesResponse; customerID: string}) {
	const [expandedPanel, setExpandedPanel] = React.useState<number | false>(false)

	const changeExpandedPanelTo = (panel: number) => (_: any, currentlyExpanded: boolean) => {
		setExpandedPanel(currentlyExpanded ? panel : false)
	}

	const activityPanel = (activity: CustomerActivity, panelId: number) => {
		const expanded = expandedPanel === panelId
		switch (activity.Type) {
			case "CustomerCreated":
				return CreatedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "SMSSent":
				return SMSSentActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "SMSFailed":
				return SMSFailedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "WelcomeSMSSent":
				return SMSSentActivity(panelId, activity, expanded, changeExpandedPanelTo, classes, false)
			case "WelcomeSMSFailed":
				return SMSFailedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "HelpSMSSent":
				return SMSSentActivity(panelId, activity, expanded, changeExpandedPanelTo, classes, false)
			case "HelpSMSFailed":
				return SMSFailedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "EmailSent":
				return EmailSentActivity(panelId, activity, expanded, changeExpandedPanelTo, classes, customerID)
			case "NOASent":
				return EmailSentActivity(panelId, activity, expanded, changeExpandedPanelTo, classes, customerID)
			case "NOALetterSent":
				return NOALetterSentActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerAddressChanged":
				return AddressChangedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerCaseEscalated":
				return CaseEscalatedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerCaseSubmitted":
				return CaseSubmittedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes, customerID)
			case "CustomerCaseResolved":
				return CaseResolvedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes, customerID)
			case "CustomerCommunicationSnoozed":
				return CommunicationSnoozedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerEmailAdded":
				return EmailAddedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerEmailRemoved":
				return EmailRemovedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerPhoneAdded":
				return PhoneAddedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerPhoneIdentityNotMatchedByScrub":
				return PhoneIdentityScrubActivity(panelId, activity, expanded, changeExpandedPanelTo, classes, false)
			case "CustomerPhoneIdentityMatchedByScrub":
				return PhoneIdentityScrubActivity(panelId, activity, expanded, changeExpandedPanelTo, classes, true)
			case "CustomerPhoneHasBeenReassigned":
				return PhoneReassignedNumberActivity(panelId, activity, expanded, changeExpandedPanelTo, classes, true)
			case "CustomerPhoneHasNotBeenReassigned":
				return PhoneReassignedNumberActivity(panelId, activity, expanded, changeExpandedPanelTo, classes, false)
			case "CustomerPhoneRemoved":
				return PhoneRemovedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerPhoneOptedOutForSMS":
				return PhoneOptedOutForSMSActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerInformationUpdated":
				return InformationUpdatedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerEmailCommunicationsOptedOut":
				return EmailCommunicationsOptedOutActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerEmailCommunicationsOptedIn":
				return EmailCommunicationsOptedInActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerPreferredLanguageConfigured":
				return PreferredLanguageConfiguredActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerDigitalCommunicationConsentGranted":
				return DigitalCommunicationConsentGrantedActivity(
					panelId,
					activity,
					expanded,
					changeExpandedPanelTo,
					classes,
				)
			case "ESIGNConsentRequested":
				return ESIGNConsentRequestedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "ESIGNConsentGranted":
				return ESIGNConsentGrantedActivity(panelId, activity, expanded, classes)
			case "ESIGNConsentRevoked":
				return ESIGNConsentRevokedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerDigitalCommunicationConsentDenied":
				return DigitalCommunicationConsentDeniedActivity(
					panelId,
					activity,
					expanded,
					changeExpandedPanelTo,
					classes,
				)
			case "CustomerEmailLeadsAddedByDataWashing":
				return EmailLeadsAddedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerEmailLeadRemovedByCustomer":
				return EmailLeadRemovedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerEmailLeadRemovedByThirdParty":
				return EmailLeadRemovedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "PaymentPlanSetupSucceeded":
				return PaymentPlanSetupSucceededActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "PaymentPlanCompleted":
				return PaymentPlanCompletedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "PaymentPlanCancelled":
				return PaymentPlanCancelledActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "PaymentArrangementSetupSucceeded":
				return PaymentArrangementSetupSucceededActivity(
					panelId,
					activity,
					expanded,
					changeExpandedPanelTo,
					classes,
				)
			case "PaymentArrangementCompleted":
				return PaymentArrangementCompletedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "PaymentArrangementCancelled":
				return PaymentArrangementCancelledActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerInstalmentOverrideApplied":
				return InstalmentOverrideAppliedActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			case "CustomerInstalmentOverrideReset":
				return InstalmentOverrideResetActivity(panelId, activity, expanded, changeExpandedPanelTo, classes)
			default:
				return renderAccordionJSON(
					panelId,
					activity.Type,
					activity.Title,
					activity.HappenedAt,
					activity.Payload,
				)
		}
	}

	const renderAccordionJSON = (Index: number, Type: string, Title: string, HappenedAt: string, Payload: unknown) => {
		const hasContent = JSON.stringify(Payload) != "{}"

		return hasContent
			? renderAccordionJSONContent(Index, Title, HappenedAt, Payload)
			: renderAccordionNoContent(Title, HappenedAt)
	}

	const renderAccordionJSONContent = (Index: number, Title: string, HappenedAt: string, Payload: unknown) => (
		<Accordion expanded={expandedPanel === Index} onChange={changeExpandedPanelTo(Index)}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<InfoIcon color="primary" />
				<Typography className={classes.summary}>
					{" "}
					<b>{Title}</b> - {HappenedAt}
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				{expandedPanel === Index && (
					<div className={classes.jsonContent}>
						<Typography className={classes.jsonContentInfo}>
							<pre>{JSON.stringify(Payload, null, 2)}</pre>
						</Typography>
					</div>
				)}
			</AccordionDetails>
		</Accordion>
	)

	const renderAccordionNoContent = (Title: string, HappenedAt: string) => (
		<AccordionNoContent expanded={false}>
			<AccordionSummary>
				<InfoIcon color="primary" />
				<Typography className={classes.summary}>
					{" "}
					<b>{Title}</b> - {HappenedAt}
				</Typography>
			</AccordionSummary>
		</AccordionNoContent>
	)

	return (
		<StyledActivityList>
			{activities.Activities.map((activity, panelId) => (
				<div key={panelId}>{activityPanel(activity, panelId)}</div>
			))}
		</StyledActivityList>
	)
}

const StyledActivityList = styled.div`
	width: 100%;

	.${classes.summary} {
		font-size: 0.9375rem;
		flex-shrink: 0;
		padding-left: 8px;
	}

	.${classes.activityContent} {
		padding-left: 15px;
	}

	.${classes.activityContentLabel} {
		font-size: 0.9375rem;
		flex-shrink: 0;
	}

	.${classes.activityContentValue} {
		font-size: 0.9375rem;
		color: rgba(0, 0, 0, 0.6);
		padding-bottom: 15px;
	}

	.${classes.jsonContent} {
		padding-left: 15px;
	}

	.${classes.jsonContentInfo} {
		font-size: 0.9375rem;
		color: rgba(0, 0, 0, 0.6);
		padding-bottom: 15px;
	}
`

const AccordionNoContent = styled(Accordion)`
	.MuiAccordionSummary-content {
		cursor: default;
	}
`

export {List}
